import { type FC, useState } from 'react';

import { Badge, Button, LoadingIcon } from '@eluve/components';

import { SurfaceHost } from './SurfaceHost';
import { getMiniApp, getMiniAppsForSlot } from './registry';
import type {
  MiniAppActionOverride,
  MiniAppActionState,
  MiniAppButtonDef,
  MiniAppContext,
  MiniAppDefinition,
  MiniAppSlot,
  MiniAppSurfaceDef,
} from './types';

/** Fallback for apps without live action state — no hooks, safe to call every
 *  render so `AppActions` calls a hook in a stable position regardless. */
const noActionState = (): MiniAppActionState => ({});

interface ActiveSurface {
  appId: string;
  kind: 'activate' | 'review';
}

/**
 * The single host extension point. Drop `<MiniAppActions slot=… context=… />`
 * on a page and every mini-app registered for that slot contributes its
 * Activate/Review buttons — each auto-wired to open the app's own surface in a
 * modal or side-panel. Host pages never import app internals.
 */
export const MiniAppActions: FC<{
  slot: MiniAppSlot;
  context: MiniAppContext;
  /** The composed app list, supplied by the host (e.g. `ALL_MINI_APPS`). The
   *  framework doesn't import the concrete apps — the host wires them in. */
  apps: readonly MiniAppDefinition[];
}> = ({ slot, context, apps }) => {
  const slotApps = getMiniAppsForSlot(apps, slot);
  const [active, setActive] = useState<ActiveSurface | null>(null);

  if (slotApps.length === 0) return null;

  const activeApp = active ? getMiniApp(apps, active.appId) : null;
  const activeDef: MiniAppSurfaceDef | undefined =
    activeApp && active
      ? active.kind === 'activate'
        ? activeApp.activate
        : activeApp.review
      : undefined;

  return (
    <div className="flex items-center gap-2">
      {slotApps.map((app) => (
        <AppActions
          key={app.id}
          app={app}
          context={context}
          onOpen={(kind) => setActive({ appId: app.id, kind })}
        />
      ))}

      {activeApp && activeDef ? (
        <SurfaceHost
          surface={activeDef.surface}
          title={activeDef.label}
          identity={activeApp.identity}
          open
          onOpenChange={(open) => {
            if (!open) setActive(null);
          }}
        >
          <activeDef.component
            context={context}
            onClose={() => setActive(null)}
          />
        </SurfaceHost>
      ) : null}
    </div>
  );
};

/**
 * One app's Activate/Review triggers. Isolated into its own component so the
 * app's `useActionState` hook is called once per app, unconditionally, in a
 * stable position — keeping the rules of hooks intact even as the app set or
 * the live action state changes.
 */
const AppActions: FC<{
  app: MiniAppDefinition;
  context: MiniAppContext;
  onOpen: (kind: 'activate' | 'review') => void;
}> = ({ app, context, onOpen }) => {
  const useAppActionState = app.useActionState ?? noActionState;
  const state = useAppActionState(context);
  return (
    <>
      <SurfaceButton
        def={app.activate}
        context={context}
        override={state?.activate}
        onClick={() => onOpen('activate')}
      />
      <SurfaceButton
        def={app.review}
        context={context}
        override={state?.review}
        onClick={() => onOpen('review')}
      />
      <CallbackButton
        def={app.action}
        context={context}
        override={state?.action}
      />
    </>
  );
};

/** A single Activate/Review trigger, hidden when the surface is absent, its
 *  static visibility predicate returns false, or live state hides it. A live
 *  `badge` override wins over the surface's static `badge`. */
const SurfaceButton: FC<{
  def?: MiniAppSurfaceDef;
  context: MiniAppContext;
  override?: MiniAppActionOverride;
  onClick: () => void;
}> = ({ def, context, override, onClick }) => {
  if (!def) return null;
  if (override?.hidden) return null;
  if (def.visible && !def.visible(context)) return null;
  const count = override?.badge ?? def.badge?.(context);
  return (
    <SlotButton
      label={def.label}
      // Surface buttons keep their historical semantics: a zero pill is hidden.
      count={count || undefined}
      loading={override?.loading}
      onClick={onClick}
    />
  );
};

/** An app's surface-less `action` button — click runs the app's callback
 *  (e.g. toggle its inline panel). Unlike surface buttons, an explicit badge
 *  of 0 renders ("we looked, found none" is a real state). */
const CallbackButton: FC<{
  def?: MiniAppButtonDef;
  context: MiniAppContext;
  override?: MiniAppActionOverride;
}> = ({ def, context, override }) => {
  if (!def) return null;
  if (override?.hidden) return null;
  if (def.visible && !def.visible(context)) return null;
  return (
    <SlotButton
      label={def.label}
      count={override?.badge}
      loading={override?.loading}
      onClick={() => def.onClick(context)}
    />
  );
};

/** Shared chrome for every slot button: label + spinner-or-count pill. */
const SlotButton: FC<{
  label: string;
  count?: number;
  loading?: boolean;
  onClick: () => void;
}> = ({ label, count, loading, onClick }) => (
  <Button variant="outline" size="sm" aria-busy={loading} onClick={onClick}>
    {label}
    {loading ? (
      <LoadingIcon size="xs" className="ml-2" />
    ) : count !== undefined ? (
      <Badge variant="secondary" className="ml-2">
        {count}
      </Badge>
    ) : null}
  </Button>
);
